'use client';

import { useEffect, useRef, useState } from 'react';
import AppImage from '@/components/ui/AppImage';
import { useLocale, useLocalizedPath } from '@/context/LocaleContext';
import { getPublicStrings } from '@/i18n/public';

export default function WhoWeWorkWithSection() {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef<HTMLElement>(null);
  const locale = useLocale();
  const localizePath = useLocalizedPath();
  const copy = getPublicStrings(locale).home.whoWeWorkWith;

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.2 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  return (
    <section
      id="who-we-work-with"
      ref={sectionRef}
      className="py-16 md:py-28 px-6 md:px-12 bg-white border-b border-border"
    >
      <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-12 gap-10 md:gap-16 items-center">
        <div
          className={`lg:col-span-5 transition-all duration-700 ${
            isVisible ? 'fade-in-up' : 'fade-in-up-init'
          }`}
        >
          <div className="relative aspect-[4/3] overflow-hidden rounded-2xl">
            <AppImage
              src="/assets/images/aos-who-we-work-with.jpg"
              alt={copy.imageAlt}
              fill
              sizes="(max-width: 1024px) 100vw, 40vw"
              className="w-full h-full object-cover"
            />
          </div>
        </div>

        <div
          className={`lg:col-span-7 transition-all duration-700 delay-100 ${
            isVisible ? 'fade-in-up' : 'fade-in-up-init'
          }`}
        >
          <span className="text-[10px] tracking-[0.3em] uppercase text-gold font-black">
            {copy.eyebrow}
          </span>
          <h2 className="text-4xl md:text-6xl text-foreground leading-tight font-heading font-bold mt-4 mb-8">
            {copy.title} <span className="text-gold italic">{copy.titleItalic}</span>
          </h2>
          <div className="space-y-5">
            <p className="text-lg text-foreground/80 font-light leading-relaxed">{copy.p1}</p>
            <p className="text-lg text-primary font-semibold leading-relaxed">{copy.highlight}</p>
            <p className="text-lg text-foreground/80 font-light leading-relaxed">{copy.p2}</p>
          </div>
          <a
            href={localizePath('/#services')}
            className="inline-flex mt-8 px-8 py-4 border-2 border-primary text-primary text-xs md:text-sm font-black uppercase tracking-[0.18em] hover:bg-primary hover:text-white transition-colors"
          >
            {copy.cta}
          </a>
        </div>
      </div>
    </section>
  );
}
